import { fabric } from 'fabric';

import { newLayerId } from './fabric-setup';
import type { ColorRole, CsRole, MaskKind } from './types';

/**
 * The object kit: every layer the editor, its panels and its templates create
 * goes through these factories, so they all share one set of defaults (ids,
 * roles, uniform strokes, left/top origin) and serialise the same way.
 */

export interface Box { x: number; y: number; w: number; h: number }

export interface TextOpts {
    x: number;
    y: number;
    w: number;
    size: number;
    font: string;
    weight?: number;
    color?: string;
    align?: 'left' | 'center' | 'right' | 'justify';
    /** Fabric charSpacing — thousandths of an em. */
    spacing?: number;
    lineHeight?: number;
    italic?: boolean;
    upper?: boolean;
    opacity?: number;
    name?: string;
    colorRole?: ColorRole;
    fontRole?: 'heading' | 'body';
}

export function makeText(text: string, o: TextOpts): fabric.Textbox {
    const tb = new fabric.Textbox(o.upper ? text.toUpperCase() : text, {
        left: o.x,
        top: o.y,
        width: Math.max(20, o.w),
        fontSize: o.size,
        fontFamily: o.font,
        fontWeight: (o.weight ?? 400) as any,
        fontStyle: o.italic ? 'italic' : 'normal',
        fill: o.color ?? '#1A1917',
        textAlign: o.align ?? 'left',
        charSpacing: o.spacing ?? 0,
        lineHeight: o.lineHeight ?? 1.16,
        opacity: o.opacity ?? 1,
        originX: 'left',
        originY: 'top',
        splitByGrapheme: false,
        editable: true,
        styles: {} as any,
    } as any);
    tag(tb, {
        role: 'text',
        name: o.name,
        colorRole: o.colorRole,
    });
    if (o.fontRole) (tb as any).csFontRole = o.fontRole;
    return tb;
}

export interface PillOpts extends TextOpts {
    bg: string;
    padX: number;
    padY: number;
    radius: number;
    bgRole?: ColorRole;
    /** Hairline around the plate (colour, width). */
    outline?: { color: string; width: number };
}

/**
 * A text layer drawn on a padded, rounded plate — CTA buttons, price tags, spec
 * chips. One editable Textbox; the plate is painted by the extension in
 * fabric-setup, so it grows with the words.
 */
export function makePill(text: string, o: PillOpts): fabric.Textbox {
    const tb = makeText(text, o);
    tb.set({ backgroundColor: o.bg } as any);
    (tb as any).bgPadX = o.padX;
    (tb as any).bgPadY = o.padY;
    (tb as any).bgRadius = o.radius;
    if (o.outline) {
        (tb as any).bgStroke = o.outline.color;
        (tb as any).bgStrokeWidth = o.outline.width;
    }
    fitTextWidth(tb);
    return tb;
}

/** Shrink a textbox to its widest line (plus a hair) — keeps pills tight and selection outlines honest. */
export function fitTextWidth(tb: fabric.Textbox, min = 20): void {
    const measured = (tb as any).calcTextWidth?.() || 0;
    if (measured > 0) {
        tb.set({ width: Math.max(min, Math.ceil(measured + 2)) });
        tb.setCoords();
    }
}

/** Text width when laid on one line — used to place things after a word. */
export function textWidth(tb: fabric.Textbox): number {
    return (tb as any).calcTextWidth?.() || tb.width || 0;
}

export function tag(
    o: fabric.Object,
    meta: { role?: CsRole; name?: string; colorRole?: ColorRole; locked?: boolean },
): fabric.Object {
    const any = o as any;
    if (!any.csId) any.csId = newLayerId();
    if (meta.role) any.csRole = meta.role;
    if (meta.name) any.csName = meta.name;
    if (meta.colorRole) any.csColorRole = meta.colorRole;
    if (meta.locked) setLocked(o, true);
    return o;
}

export function setLocked(o: fabric.Object, locked: boolean): void {
    const any = o as any;
    any.csLocked = locked || undefined;
    o.set({
        selectable: !locked,
        evented: !locked,
        lockMovementX: locked,
        lockMovementY: locked,
        lockScalingX: locked,
        lockScalingY: locked,
        lockRotation: locked,
        hasControls: !locked,
    } as any);
}

export interface RectOpts extends Box {
    fill?: string | fabric.Gradient;
    rx?: number;
    stroke?: string;
    strokeWidth?: number;
    dash?: number[];
    opacity?: number;
    name?: string;
    role?: CsRole;
    colorRole?: ColorRole;
}

export function makeRect(o: RectOpts): fabric.Rect {
    const r = new fabric.Rect({
        left: o.x,
        top: o.y,
        width: o.w,
        height: o.h,
        fill: (o.fill ?? '#1A1917') as any,
        rx: o.rx ?? 0,
        ry: o.rx ?? 0,
        stroke: o.stroke,
        strokeWidth: o.strokeWidth ?? 0,
        strokeDashArray: o.dash,
        strokeUniform: true,
        opacity: o.opacity ?? 1,
        originX: 'left',
        originY: 'top',
    } as any);
    tag(r, { role: o.role ?? 'shape', name: o.name, colorRole: o.colorRole });
    if (o.role === 'shape' || !o.role) (r as any).csShape = o.rx ? 'rounded' : 'rect';
    return r;
}

/** A vertical or horizontal linear gradient in percentage units (survives resizing). */
export function linearGradient(
    stops: Array<{ offset: number; color: string }>,
    direction: 'down' | 'up' | 'right' | 'left' = 'down',
): fabric.Gradient {
    const coords = {
        down: { x1: 0, y1: 0, x2: 0, y2: 1 },
        up: { x1: 0, y1: 1, x2: 0, y2: 0 },
        right: { x1: 0, y1: 0, x2: 1, y2: 0 },
        left: { x1: 1, y1: 0, x2: 0, y2: 0 },
    }[direction];
    return new fabric.Gradient({
        type: 'linear',
        gradientUnits: 'percentage',
        coords,
        colorStops: stops,
    } as any);
}

/**
 * A legibility scrim — the gradient a designer lays under type on a photo.
 * The ramp is eased (not linear) so it never shows a hard band.
 */
export function makeScrim(
    kind: 'bottom' | 'top' | 'left' | 'right' | 'full',
    W: number,
    H: number,
    strength = 0.62,
    color = '#000000',
    coverage = 0.6,
): fabric.Rect {
    const c = (a: number) => {
        const m = color.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
        const [r, g, b] = m ? [1, 2, 3].map((i) => parseInt(m[i], 16)) : [0, 0, 0];
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    };
    const ramp = [
        { offset: 0, color: c(0) },
        { offset: 0.3, color: c(strength * 0.18) },
        { offset: 0.6, color: c(strength * 0.55) },
        { offset: 1, color: c(strength) },
    ];
    let box: Box;
    let dir: 'down' | 'up' | 'right' | 'left';
    if (kind === 'bottom') { box = { x: 0, y: H * (1 - coverage), w: W, h: H * coverage }; dir = 'down'; }
    else if (kind === 'top') { box = { x: 0, y: 0, w: W, h: H * coverage }; dir = 'up'; }
    else if (kind === 'left') { box = { x: 0, y: 0, w: W * coverage, h: H }; dir = 'left'; }
    else if (kind === 'right') { box = { x: W * (1 - coverage), y: 0, w: W * coverage, h: H }; dir = 'right'; }
    else {
        return makeRect({ x: 0, y: 0, w: W, h: H, fill: c(strength * 0.6), role: 'scrim', name: 'Overlay' });
    }
    const r = makeRect({ x: box.x, y: box.y, w: box.w, h: box.h, fill: linearGradient(ramp, dir), role: 'scrim', name: 'Scrim' });
    return r;
}

export interface LineOpts {
    color?: string;
    width?: number;
    dash?: number[];
    name?: string;
    role?: CsRole;
    colorRole?: ColorRole;
    opacity?: number;
}

export function makeLine(x1: number, y1: number, x2: number, y2: number, o: LineOpts = {}): fabric.Line {
    const l = new fabric.Line([x1, y1, x2, y2], {
        stroke: o.color ?? '#1A1917',
        strokeWidth: o.width ?? 2,
        strokeDashArray: o.dash,
        strokeLineCap: o.dash ? 'butt' : 'round',
        strokeUniform: true,
        opacity: o.opacity ?? 1,
    } as any);
    tag(l, { role: o.role ?? 'divider', name: o.name, colorRole: o.colorRole });
    (l as any).csShape = o.dash ? 'dashed' : 'line';
    return l;
}

// ─── Images ─────────────────────────────────────────────────────────────

const imageCache = new Map<string, Promise<HTMLImageElement>>();

/**
 * Load an image once per URL, CORS-clean so the canvas can still export.
 * Falls back to a tainted load rather than nothing, so the vendor at least
 * sees the picture (export then fails loudly, which is the honest outcome).
 */
let customLoader: ((url: string) => Promise<HTMLImageElement>) | null = null;

/** Swap how pictures are fetched (tests); returns a function that restores the default. */
export function setImageLoader(fn: (url: string) => Promise<HTMLImageElement>): () => void {
    const prev = customLoader;
    customLoader = fn;
    return () => { customLoader = prev; };
}

export function loadImageElement(url: string): Promise<HTMLImageElement> {
    if (customLoader) return customLoader(url);
    const hit = imageCache.get(url);
    if (hit) return hit;
    const p = new Promise<HTMLImageElement>((resolve, reject) => {
        const attempt = (cors: boolean) => {
            const img = new Image();
            if (cors && !url.startsWith('blob:') && !url.startsWith('data:')) img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => {
                if (cors) attempt(false);
                else reject(new Error(`Could not load ${url}`));
            };
            img.src = url;
        };
        attempt(true);
    });
    imageCache.set(url, p);
    p.catch(() => imageCache.delete(url));
    return p;
}

/** Natural size of a fabric image's source, independent of its crop. */
export function naturalSize(img: fabric.Image): { w: number; h: number } {
    const el: any = (img as any)._originalElement || (img as any)._element || img.getElement?.();
    return {
        w: el?.naturalWidth || el?.width || img.width || 1,
        h: el?.naturalHeight || el?.height || img.height || 1,
    };
}

/**
 * Fill a box with the picture, cropping the overflow (CSS object-fit: cover).
 * Uses Fabric's own crop (cropX / cropY / width / height) rather than a clip
 * path, so the layer IS the box: it moves, aligns and snaps as what you see.
 * `focus` picks which part survives the crop (0..1 per axis); `zoom` > 1 crops
 * tighter, for detail swatches cut from one photo.
 */
export function coverFit(
    img: fabric.Image,
    box: Box,
    focus: { x: number; y: number } = { x: 0.5, y: 0.5 },
    zoom = 1,
): void {
    const { w: nw, h: nh } = naturalSize(img);
    const scale = Math.max(box.w / nw, box.h / nh) * Math.max(1, zoom);
    const sw = Math.min(nw, box.w / scale);
    const sh = Math.min(nh, box.h / scale);
    img.set({
        cropX: Math.max(0, (nw - sw) * focus.x),
        cropY: Math.max(0, (nh - sh) * focus.y),
        width: sw,
        height: sh,
        scaleX: scale,
        scaleY: scale,
        left: box.x,
        top: box.y,
        originX: 'left',
        originY: 'top',
        angle: 0,
    } as any);
    img.setCoords();
    refreshMask(img);
}

/** Fit the whole picture inside a box (object-fit: contain), no crop. */
export function containFit(img: fabric.Image, box: Box, align: 'center' | 'start' | 'end' = 'center'): void {
    const { w: nw, h: nh } = naturalSize(img);
    const scale = Math.min(box.w / nw, box.h / nh);
    const w = nw * scale;
    const h = nh * scale;
    const off = (free: number) => (align === 'start' ? 0 : align === 'end' ? free : free / 2);
    img.set({
        cropX: 0,
        cropY: 0,
        width: nw,
        height: nh,
        scaleX: scale,
        scaleY: scale,
        left: box.x + off(box.w - w),
        top: box.y + off(box.h - h),
        originX: 'left',
        originY: 'top',
    } as any);
    img.setCoords();
}

/** The clip path for a mask, in the image's own (unscaled, centred) space. */
export function buildMask(kind: MaskKind, w: number, h: number, radius: number): fabric.Object | undefined {
    if (kind === 'none') return undefined;
    if (kind === 'circle') {
        return new fabric.Ellipse({ rx: w / 2, ry: h / 2, originX: 'center', originY: 'center', left: 0, top: 0 } as any);
    }
    if (kind === 'rounded') {
        const r = Math.max(0, Math.min(radius, w / 2, h / 2));
        return new fabric.Rect({ width: w, height: h, rx: r, ry: r, originX: 'center', originY: 'center', left: 0, top: 0 } as any);
    }
    // Arch: a semicircle on top of a rectangle — the frame of every interior
    // brand's mood page.
    const r = Math.min(w / 2, h);
    const x0 = -w / 2;
    const y0 = -h / 2;
    const d = `M ${x0} ${y0 + h} L ${x0} ${y0 + r} A ${r} ${r} 0 0 1 ${x0 + w} ${y0 + r} L ${x0 + w} ${y0 + h} Z`;
    return new fabric.Path(d, { originX: 'center', originY: 'center', left: 0, top: 0 } as any);
}

/** Apply (or clear) a mask. Radius is in artboard pixels, converted to the image's own units. */
export function applyMask(img: fabric.Image, kind: MaskKind, radiusPx?: number): void {
    const any = img as any;
    any.csMask = kind === 'none' ? undefined : kind;
    if (radiusPx !== undefined) any.csMaskRadius = radiusPx;
    refreshMask(img);
}

/** Rebuild the mask after a crop or resize changed the image's own size. */
export function refreshMask(img: fabric.Image): void {
    const any = img as any;
    const kind: MaskKind = any.csMask || 'none';
    if (kind === 'none') {
        if (img.clipPath && !(img.clipPath as any).absolutePositioned) img.set({ clipPath: undefined } as any);
        return;
    }
    const scale = img.scaleX || 1;
    const radius = (any.csMaskRadius ?? 24) / scale;
    img.set({ clipPath: buildMask(kind, img.width || 1, img.height || 1, radius) } as any);
    img.dirty = true;
}

/**
 * A fabric image over a loaded element. `crossOrigin` is set explicitly:
 * Fabric serialises it, and a scene reloaded without it fetches the picture
 * without CORS — which taints the canvas and makes Save throw.
 */
export function newImage(el: HTMLImageElement, extra: Record<string, any> = {}): fabric.Image {
    return new fabric.Image(el, {
        originX: 'left',
        originY: 'top',
        crossOrigin: el.crossOrigin ? 'anonymous' : null,
        ...extra,
    } as any);
}

export interface ImageOpts extends Partial<Box> {
    mask?: MaskKind;
    maskRadius?: number;
    role?: CsRole;
    name?: string;
    focus?: { x: number; y: number };
    zoom?: number;
    fit?: 'cover' | 'contain';
    opacity?: number;
}

export async function makeImage(url: string, o: ImageOpts = {}): Promise<fabric.Image> {
    const el = await loadImageElement(url);
    const img = newImage(el, { opacity: o.opacity ?? 1 });
    tag(img, { role: o.role ?? 'image', name: o.name });
    if (o.w && o.h) {
        const box = { x: o.x ?? 0, y: o.y ?? 0, w: o.w, h: o.h };
        if (o.fit === 'contain') containFit(img, box);
        else coverFit(img, box, o.focus, o.zoom);
    } else {
        img.set({ left: o.x ?? 0, top: o.y ?? 0 });
    }
    if (o.mask && o.mask !== 'none') applyMask(img, o.mask, o.maskRadius);
    return img;
}

export type Anchor = 'tl' | 'tc' | 'tr' | 'cl' | 'c' | 'cr' | 'bl' | 'bc' | 'br';

/**
 * A logo, contained in a box and pinned to one of its corners — a wide
 * wordmark stays wide, a square monogram stays square.
 */
export async function makeLogo(url: string, box: Box, anchor: Anchor = 'tl'): Promise<fabric.Image> {
    const el = await loadImageElement(url);
    const img = newImage(el);
    tag(img, { role: 'logo', name: 'Logo' });
    const nw = el.naturalWidth || 1;
    const nh = el.naturalHeight || 1;
    const scale = Math.min(box.w / nw, box.h / nh);
    const w = nw * scale;
    const h = nh * scale;
    const col = anchor[anchor.length - 1];
    const row = anchor[0];
    const left = col === 'l' ? box.x : col === 'r' ? box.x + box.w - w : box.x + (box.w - w) / 2;
    const top = row === 't' ? box.y : row === 'b' ? box.y + box.h - h : box.y + (box.h - h) / 2;
    img.set({ left, top, scaleX: scale, scaleY: scale });
    img.setCoords();
    return img;
}

// ─── SVG (icons, decorative marks) ──────────────────────────────────────

/**
 * Turn an SVG string into one Fabric group — icons stay one layer that scales,
 * recolours and serialises as plain paths. Stroke/fill are forced onto every
 * child because Fabric's attribute inheritance from the <svg> root is partial.
 */
export function makeSvgGroup(
    svg: string,
    o: { x: number; y: number; size: number; color: string; strokeWidth?: number; filled?: boolean; name?: string; role?: CsRole },
): Promise<fabric.Group> {
    return new Promise((resolve) => {
        fabric.loadSVGFromString(svg, (objects, options) => {
            const kids = (objects || []).filter(Boolean);
            kids.forEach((k: any) => {
                if (o.filled) {
                    k.set({ fill: o.color, stroke: undefined, strokeWidth: 0 });
                } else {
                    k.set({
                        fill: '',
                        stroke: o.color,
                        strokeWidth: o.strokeWidth ?? 1.75,
                        strokeLineCap: 'round',
                        strokeLineJoin: 'round',
                    });
                }
            });
            const group = fabric.util.groupSVGElements(kids, options) as fabric.Group;
            // A single path comes back as that path, not a group — wrap it so
            // every icon is the same kind of layer.
            const g = group.type === 'group' ? group : new fabric.Group([group as any]);
            const vb = (options as any)?.viewBoxWidth || (options as any)?.width || 24;
            const scale = o.size / vb;
            g.set({ left: o.x, top: o.y, scaleX: scale, scaleY: scale, originX: 'left', originY: 'top' });
            tag(g, { role: o.role ?? 'icon', name: o.name });
            g.setCoords();
            resolve(g);
        });
    });
}

/** Recolour an icon / vector group: strokes stay strokes, fills stay fills. */
export function recolorGroup(g: fabric.Object, color: string): void {
    const walk = (o: any) => {
        if (typeof o.getObjects === 'function') { o.getObjects().forEach(walk); return; }
        if (o.stroke && o.stroke !== 'none') o.set('stroke', color);
        if (o.fill && o.fill !== 'none' && o.fill !== '') o.set('fill', color);
    };
    walk(g);
    (g as any).dirty = true;
}

/** The colour an icon group currently wears (first stroke or fill found). */
export function groupColor(g: fabric.Object): string {
    let found = '';
    const walk = (o: any) => {
        if (found) return;
        if (typeof o.getObjects === 'function') { o.getObjects().forEach(walk); return; }
        if (typeof o.stroke === 'string' && o.stroke && o.stroke !== 'none') found = o.stroke;
        else if (typeof o.fill === 'string' && o.fill && o.fill !== 'none') found = o.fill;
    };
    walk(g);
    return found || '#1A1917';
}
